import {forwardRef, useState} from "react";
import {IconButton, Typography} from "@mui/material";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import {UserBadge} from "./UserBadge";
import {SelectUserDialog} from "./SelectUserDialog";

/**
 * UserPicker — wraps UserBadge with an optional existing-user picker dialog.
 *
 * Props: same as UserBadge, plus:
 *   existingUsers — { platform: [...], contacts: [...] }  from fetchAllCustomerContacts
 *                   OR flat array (backwards compat for platform-only contexts)
 *   onRefresh     — optional () => void — called when refresh button clicked in dialog
 */

const deduplicateList = (list) => {
    const map = {};
    (list || []).forEach(u => {
        const key = u.uuid || u.email || JSON.stringify(u);
        map[key] = u;
    });
    return Object.values(map);
};

const normalise = (existingUsers) => {
    if (!existingUsers) return { platform: [], contacts: [] };
    if (Array.isArray(existingUsers)) return { platform: deduplicateList(existingUsers), contacts: [] };
    return {
        platform: deduplicateList(existingUsers.platform),
        contacts: deduplicateList(existingUsers.contacts),
    };
};

export const UserPicker = forwardRef(({
    value,
    onChange,
    onSave,
    onRefresh,
    label,
    fields,
    canToggle = true,
    active = true,
    existingUsers,
    size,
    requiredFields,
}, ref) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const {platform, contacts} = normalise(existingUsers);
    const hasExisting = active && (platform.length > 0 || contacts.length > 0);

    const handleSelect = (user) => {
        if (onChange) onChange(user);
        setDialogOpen(false);
    };

    const handleRefresh = onRefresh ? () => {
        onRefresh();
    } : undefined;

    const peopleButton = hasExisting ? (
        <IconButton
            size="small"
            onClick={() => setDialogOpen(true)}
            sx={{p: 0.25, color: '#FFC540', '&:hover': {opacity: 0.8}, pointerEvents: 'auto'}}
        >
            <PeopleOutlinedIcon sx={{fontSize: 14}}/>
            <Typography sx={{fontSize: 11, fontFamily: 'Poppins', ml: 0.25, color: 'inherit'}}>
                Pick user
            </Typography>
        </IconButton>
    ) : null;

    return (
        <>
            <UserBadge
                ref={ref}
                value={value}
                onChange={onChange}
                onSave={onSave}
                label={label}
                fields={fields}
                canToggle={canToggle}
                active={active}
                actions={peopleButton}
                size={size}
                requiredFields={requiredFields}
            />
            {hasExisting && (
                <SelectUserDialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    users={{platform, contacts}}
                    onConfirm={handleSelect}
                    onRefresh={handleRefresh}
                />
            )}
        </>
    );
});
